import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Loader2, Bot, Zap, MessageSquare, Trash2, Copy, ExternalLink,
  Hotel, Stethoscope, Utensils, Dumbbell, Scissors, Building2, ArrowRight, Link2, Check, Eye,
  Calendar as CalendarIcon, IndianRupee, Code, X, Info
} from "lucide-react";

const ICONS = { hotel: Hotel, stethoscope: Stethoscope, utensils: Utensils, dumbbell: Dumbbell, scissors: Scissors, building: Building2 };

export default function Automations() {
  const [prompt, setPrompt] = useState("");
  const [creating, setCreating] = useState(false);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justCreated, setJustCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([api.get("/automations"), api.get("/templates")]);
      setItems(a.data); setTemplates(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (text, template_id) => {
    if (!text || text.trim().length < 5) { toast.error("Describe what you want to automate"); return; }
    setCreating(true);
    try {
      const r = await api.post("/automations", { prompt: text, template_id });
      setItems((prev) => [r.data, ...prev]);
      setPrompt("");
      setJustCreated(r.data);
      toast.success("AI agent created. Share your link!");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setCreating(false); }
  };

  const toggle = async (a) => {
    const r = await api.patch(`/automations/${a.id}`, { is_active: !a.is_active });
    setItems((prev) => prev.map((x) => x.id === a.id ? r.data : x));
  };
  const saveIcal = async (a, ical_url) => {
    const r = await api.patch(`/automations/${a.id}`, { ical_url });
    setItems((prev) => prev.map((x) => x.id === a.id ? r.data : x));
    toast.success(ical_url ? "Calendar linked" : "Calendar removed");
  };
  const savePayments = async (a, payments) => {
    const r = await api.patch(`/automations/${a.id}`, { payments });
    setItems((prev) => prev.map((x) => x.id === a.id ? r.data : x));
    toast.success(payments.enabled ? "Payments enabled" : "Payments saved");
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? Its public link will stop working.`)) return;
    await api.delete(`/automations/${a.id}`);
    setItems((prev) => prev.filter((x) => x.id !== a.id));
    toast.success("Agent removed");
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="automations-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">AI Agents</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">Type. Generate. Share the link.</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl text-sm">Describe your business in plain English. We generate a public AI chat agent with its own shareable link. Customers chat with it, AI captures every detail into your admin panel.</p>
      </div>

      <div className="ai-border rounded-2xl bg-zinc-950 p-1 mt-8">
        <div className="rounded-xl bg-black p-5">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Prompt → AI Agent
          </div>
          <Textarea
            placeholder='e.g. "Hotel booking agent for Ragavi Hotels. Deluxe ₹3,500/night, Suite ₹5,500/night. Check-in 2pm. Ask check-in date, nights and number of guests. Capture name and phone."'
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            className="bg-transparent border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 resize-none"
            data-testid="automation-prompt"
          />
          <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-zinc-500">Tip: include pricing, hours, and what data the AI should ask the customer.</div>
            <Button onClick={() => create(prompt)} disabled={creating}
              className="bg-white text-black hover:bg-zinc-200 h-10 px-5" data-testid="generate-automation-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Generating…</> : <>Generate agent <ArrowRight className="w-4 h-4 ml-2"/></>}
            </Button>
          </div>
        </div>
      </div>

      {justCreated && <JustCreatedBanner agent={justCreated} onDismiss={() => setJustCreated(null)} />}

      <div className="mt-10">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Quick start templates</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {templates.map((t) => {
            const Icon = ICONS[t.icon] || Bot;
            return (
              <button key={t.id} onClick={() => create(t.prompt, t.id)} disabled={creating}
                className="text-left bg-zinc-950 border border-white/10 rounded-xl p-4 hover:border-emerald-500/30 hover:bg-emerald-500/[0.03] transition group disabled:opacity-50"
                data-testid={`tpl-${t.id}`}>
                <Icon className="w-5 h-5 text-emerald-400 mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-display text-sm tracking-tight">{t.title}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{t.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Your agents · {items.length}</div>
        </div>
        {loading ? <SkeletonGrid />
          : items.length === 0 ? (
            <div className="bg-zinc-950 border border-white/10 rounded-xl p-12 text-center" data-testid="no-automations">
              <Bot className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <div className="font-display text-lg">No agents yet.</div>
              <div className="text-zinc-500 text-sm">Type a prompt above or pick a template.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence>
                {items.map((a) => <AgentCard key={a.id} a={a} onToggle={toggle} onRemove={remove} onSaveIcal={saveIcal} onSavePayments={savePayments} />)}
              </AnimatePresence>
            </div>
          )}
      </div>
    </div>
  );
}

function JustCreatedBanner({ agent, onDismiss }) {
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="mt-6 bg-emerald-500/[0.05] border border-emerald-500/30 rounded-xl p-5 ai-glow" data-testid="just-created-banner">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-emerald-300 mb-1">Agent live · share this link</div>
          <div className="font-display text-xl tracking-tight">{agent.name}</div>
          <CopyLink url={agent.public_url} className="mt-3" />
        </div>
        <div className="flex items-center gap-2">
          <a href={agent.public_url} target="_blank" rel="noreferrer" data-testid="preview-just-created">
            <Button variant="outline" className="bg-white text-black hover:bg-zinc-200 h-10"><Eye className="w-4 h-4 mr-1.5"/> Preview chat</Button>
          </a>
          <button onClick={onDismiss} className="text-zinc-500 hover:text-white text-sm">Dismiss</button>
        </div>
      </div>
    </motion.div>
  );
}

function CopyLink({ url, className = "" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className={`inline-flex items-center gap-2 bg-black border border-white/10 rounded-md px-3 h-10 max-w-full ${className}`}>
      <Link2 className="w-3.5 h-3.5 text-emerald-400 shrink-0"/>
      <span className="font-mono text-xs text-zinc-300 truncate">{url}</span>
      <button onClick={copy} className="ml-1 text-zinc-400 hover:text-white" data-testid="copy-link-btn">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400"/> : <Copy className="w-3.5 h-3.5"/>}
      </button>
    </div>
  );
}

function AgentCard({ a, onToggle, onRemove, onSaveIcal, onSavePayments }) {
  const isHotel = (a.spec?.industry || "").toLowerCase() === "hotel";
  const [embedOpen, setEmbedOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="bg-zinc-950 border border-white/10 rounded-xl p-5 hover:border-white/20 transition" data-testid={`automation-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px] tracking-widest uppercase">
            {a.spec?.industry || "general"}
          </Badge>
          <div className="font-display text-lg tracking-tight mt-2 truncate">{a.name}</div>
          <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{a.spec?.intent_summary}</div>
        </div>
        <Switch checked={a.is_active} onCheckedChange={() => onToggle(a)} data-testid={`toggle-${a.id}`} />
      </div>

      <CopyLink url={a.public_url} className="mt-4 max-w-full w-full"/>

      {isHotel && <IcalRow a={a} onSave={onSaveIcal} />}
      <PaymentsRow a={a} onSave={onSavePayments} />

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Chats" value={a.stats?.chats_started || 0} icon={MessageSquare} />
        <Stat label="Leads" value={a.stats?.leads_captured || 0} icon={Zap} />
        <Stat label="Msgs" value={a.stats?.messages_handled || 0} icon={Bot} />
      </div>

      <div className="mt-4 bg-black/40 border border-white/5 rounded-md p-3 text-xs text-zinc-400 italic line-clamp-3">"{a.spec?.welcome_message}"</div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <a href={a.public_url} target="_blank" rel="noreferrer" data-testid={`preview-${a.id}`}>
          <Button size="sm" variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 h-8">
            <ExternalLink className="w-3 h-3 mr-1.5" /> Preview
          </Button>
        </a>
        <Button size="sm" variant="outline" onClick={() => setEmbedOpen(true)} className="bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 h-8" data-testid={`embed-${a.id}`}>
          <Code className="w-3 h-3 mr-1.5" /> Embed
        </Button>
        <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-8 ml-auto" onClick={() => onRemove(a)} data-testid={`delete-${a.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {embedOpen && <EmbedModal a={a} onClose={() => setEmbedOpen(false)} />}
    </motion.div>
  );
}

function PaymentsRow({ a, onSave }) {
  const p = a.payments || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    enabled: p.enabled || false,
    key_id: p.key_id || "",
    key_secret: "",
    advance_amount: p.advance_amount || 0,
    payment_label: p.payment_label || "Booking advance",
  });
  const [busy, setBusy] = useState(false);
  const configured = Boolean(p.enabled);

  const submit = async (enable) => {
    setBusy(true);
    try {
      const payload = { ...form, enabled: enable };
      // Only send secret if user typed a new one; empty means keep existing
      if (!form.key_secret) delete payload.key_secret;
      await onSave(a, payload);
      setOpen(false);
      setForm((f) => ({ ...f, key_secret: "" }));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 rounded-md px-3 h-9 text-xs border transition ${configured
          ? "bg-emerald-500/[0.06] border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/[0.1]"
          : "bg-black/40 border-white/10 text-zinc-400 hover:border-white/20 hover:text-white"}`}
        data-testid={`payments-toggle-${a.id}`}>
        <IndianRupee className="w-3.5 h-3.5"/>
        <span className="font-mono truncate flex-1 text-left">
          {configured
            ? `Collect ₹${p.advance_amount} advance · ${p.mode || "test"}`
            : "Collect advance via Razorpay (per booking)"}
        </span>
        {configured && <Check className="w-3 h-3"/>}
      </button>
      {open && (
        <div className="mt-2 bg-black/60 border border-white/10 rounded-md p-3 space-y-3" data-testid={`payments-panel-${a.id}`}>
          <div className="flex items-start gap-2 text-[10px] text-zinc-500 leading-relaxed bg-white/[0.02] border border-white/10 rounded p-2">
            <Info className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0"/>
            <span>Money settles <span className="text-white">directly to your Razorpay account</span>. We never touch or hold your funds. Get keys from Razorpay Dashboard → Settings → API Keys.</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Key ID</div>
              <input value={form.key_id} onChange={(e) => setForm({ ...form, key_id: e.target.value })}
                placeholder="rzp_test_xxxxxxxx or rzp_live_xxxxxxxx"
                className="w-full bg-transparent border border-white/10 rounded px-2 h-9 text-xs focus:border-white/30 outline-none font-mono text-white"
                data-testid={`rzp-key-id-${a.id}`}/>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">
                Key Secret {p.key_secret_set && <span className="text-emerald-400 normal-case tracking-normal">· stored</span>}
              </div>
              <input type="password" value={form.key_secret} onChange={(e) => setForm({ ...form, key_secret: e.target.value })}
                placeholder={p.key_secret_set ? "•••••••• (keep existing)" : "your Razorpay secret"}
                className="w-full bg-transparent border border-white/10 rounded px-2 h-9 text-xs focus:border-white/30 outline-none font-mono text-white"
                data-testid={`rzp-key-secret-${a.id}`}/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Advance amount (₹)</div>
              <input type="number" min={1} value={form.advance_amount}
                onChange={(e) => setForm({ ...form, advance_amount: parseInt(e.target.value || "0", 10) })}
                className="w-full bg-transparent border border-white/10 rounded px-2 h-9 text-xs focus:border-white/30 outline-none text-white"
                data-testid={`rzp-amount-${a.id}`}/>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Button label</div>
              <input value={form.payment_label} onChange={(e) => setForm({ ...form, payment_label: e.target.value })}
                placeholder="Booking advance"
                className="w-full bg-transparent border border-white/10 rounded px-2 h-9 text-xs focus:border-white/30 outline-none text-white"
                data-testid={`rzp-label-${a.id}`}/>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={() => submit(true)} disabled={busy || !form.key_id || (!form.key_secret && !p.key_secret_set) || !form.advance_amount}
              className="h-8 bg-white text-black hover:bg-zinc-200 flex-1" data-testid={`rzp-save-${a.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : (configured ? "Update & keep on" : "Enable payments")}
            </Button>
            {configured && (
              <Button size="sm" variant="ghost" onClick={() => submit(false)} disabled={busy} className="h-8 text-zinc-400 hover:bg-white/5" data-testid={`rzp-disable-${a.id}`}>
                Turn off
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EmbedModal({ a, onClose }) {
  const [mode, setMode] = useState("bubble");
  const origin = window.location.origin;
  const snippet = mode === "bubble"
    ? `<script src="${origin}/widget.js" data-slug="${a.slug}" data-mode="bubble" data-accent="#10b981" async></script>`
    : `<script src="${origin}/widget.js" data-slug="${a.slug}" data-mode="inline" data-height="640" async></script>`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(snippet); setCopied(true); toast.success("Embed code copied"); setTimeout(() => setCopied(false), 1600); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid={`embed-modal-${a.id}`}>
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-emerald-400 mb-1">Embed anywhere</div>
            <div className="font-display text-2xl tracking-tighter">One-line install</div>
            <div className="text-xs text-zinc-500 mt-1">Paste this script tag before the closing &lt;/body&gt; of any website.</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
        </div>

        <div className="grid grid-cols-2 gap-2 p-1 bg-black/60 border border-white/10 rounded-lg mb-3">
          {["bubble", "inline"].map((m) => (
            <button key={m} onClick={() => setMode(m)}
              className={`h-9 rounded-md text-xs tracking-widest uppercase transition ${mode === m ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
              data-testid={`embed-mode-${m}-${a.id}`}>
              {m === "bubble" ? "Chat bubble" : "Inline embed"}
            </button>
          ))}
        </div>

        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">
          {mode === "bubble"
            ? "Floating button in the bottom-right, opens the chat on click"
            : "Renders the chat inline exactly where you place the script"}
        </div>
        <div className="bg-black border border-white/10 rounded-md p-3 font-mono text-[11px] text-zinc-300 whitespace-pre-wrap break-all" data-testid={`embed-snippet-${a.id}`}>
          {snippet}
        </div>

        <Button onClick={copy} className="mt-4 w-full bg-emerald-500 text-black hover:bg-emerald-400 h-10" data-testid={`embed-copy-${a.id}`}>
          {copied ? <><Check className="w-4 h-4 mr-1.5"/> Copied</> : <><Copy className="w-4 h-4 mr-1.5"/> Copy embed code</>}
        </Button>
      </div>
    </div>
  );
}

function IcalRow({ a, onSave }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(a.ical_url || "");
  const [busy, setBusy] = useState(false);
  const linked = Boolean(a.ical_url);

  const save = async () => {
    setBusy(true);
    try { await onSave(a, url.trim()); setOpen(false); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await onSave(a, ""); setUrl(""); setOpen(false); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 rounded-md px-3 h-9 text-xs border transition ${linked
          ? "bg-emerald-500/[0.06] border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/[0.1]"
          : "bg-black/40 border-white/10 text-zinc-400 hover:border-white/20 hover:text-white"}`}
        data-testid={`ical-toggle-${a.id}`}>
        <CalendarIcon className="w-3.5 h-3.5"/>
        <span className="font-mono truncate flex-1 text-left">
          {linked ? "Calendar linked — AI knows availability" : "Link Google / Airbnb / iCloud calendar"}
        </span>
        {linked && <Check className="w-3 h-3"/>}
      </button>
      {open && (
        <div className="mt-2 bg-black/60 border border-white/10 rounded-md p-3 space-y-2" data-testid={`ical-panel-${a.id}`}>
          <div className="text-[10px] tracking-widest uppercase text-zinc-500">Paste your .ics URL</div>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://calendar.google.com/calendar/ical/..."
            className="w-full bg-transparent border border-white/10 rounded px-3 h-9 text-xs focus:border-white/30 outline-none font-mono text-white"
            data-testid={`ical-input-${a.id}`}
          />
          <div className="text-[10px] text-zinc-500 leading-relaxed">
            Google Calendar: Settings → your calendar → <em>Secret address in iCal format</em>.
            Airbnb: Calendar → Availability settings → Export calendar. The AI will honestly answer availability questions based on these booked dates.
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={save} disabled={busy || !url.trim()} className="h-8 bg-white text-black hover:bg-zinc-200 flex-1" data-testid={`ical-save-${a.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : linked ? "Update" : "Link calendar"}
            </Button>
            {linked && (
              <Button size="sm" variant="ghost" onClick={clear} disabled={busy} className="h-8 text-red-400 hover:bg-red-500/10" data-testid={`ical-clear-${a.id}`}>
                Remove
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-black/30 border border-white/5 rounded-md p-2">
      <div className="text-[9px] tracking-widest uppercase text-zinc-500">{label}</div>
      <div className="flex items-center gap-1.5 mt-0.5"><Icon className="w-3 h-3 text-emerald-400" /><span className="font-display text-base">{value}</span></div>
    </div>
  );
}

function SkeletonGrid() {
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="bg-zinc-950 border border-white/10 rounded-xl p-5 h-56 shimmer"/>)}</div>;
}
